import { NavLink, Outlet, useNavigate, Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { LayoutDashboard, Users, MessagesSquare, Mail, Settings as SettingsIcon, LogOut, MailWarning, X } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useNewLeadWatcher } from "@/lib/useNewLeadWatcher";

function EmailDeliveryBanner() {
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem("coachly_email_banner_dismissed") === "1");
  const location = useLocation();
  useEffect(() => {
    api.get("/email/status").then((r) => setStatus(r.data)).catch(() => {});
  }, [location.pathname]);
  if (!status || status.configured || dismissed) return null;
  const dismiss = () => {
    sessionStorage.setItem("coachly_email_banner_dismissed", "1");
    setDismissed(true);
  };
  return (
    <div className="flex items-start gap-3 px-6 py-3 border-b border-[#e6c89a] text-sm" style={{ background: "#fbf1dd", color: "#4a3a1a" }} data-testid="email-delivery-banner" role="status">
      <MailWarning className="w-4 h-4 mt-0.5 shrink-0 text-[#b7791f]" />
      <div className="flex-1">
        <span className="font-medium">Email delivery isn't connected yet.</span>{" "}
        Nurture sequences, lead alerts and your morning digest are being recorded but <span className="font-medium">won't reach anyone</span> until it is.{" "}
        <Link to="/app/settings#email-delivery" className="underline underline-offset-4 font-medium hover:opacity-80" data-testid="email-delivery-banner-link">Connect email →</Link>
      </div>
      <button onClick={dismiss} aria-label="Dismiss" className="p-1 rounded hover:bg-black/5" data-testid="email-delivery-banner-dismiss"><X className="w-4 h-4" /></button>
    </div>
  );
}

const links = [
  { to: "/app", label: "Overview", icon: LayoutDashboard, end: true, testId: "nav-overview" },
  { to: "/app/leads", label: "Leads", icon: Users, testId: "nav-leads" },
  { to: "/app/conversations", label: "Conversations", icon: MessagesSquare, testId: "nav-conversations" },
  { to: "/app/sequences", label: "Sequences", icon: Mail, testId: "nav-sequences" },
  { to: "/app/settings", label: "Settings", icon: SettingsIcon, testId: "nav-settings" },
];

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  useNewLeadWatcher({ enabled: !!user });

  const handleLogout = () => {
    logout();
    nav("/");
  };

  return (
    <div className="min-h-screen flex" style={{ background: "#f9f8f6" }}>
      <aside className="w-64 shrink-0 border-r border-[#e5e2da] bg-white flex flex-col" data-testid="dashboard-sidebar">
        <div className="p-6 border-b border-[#e5e2da]">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full flex items-center justify-center text-[#f9f8f6] font-serif-display text-xl" style={{ background: "#163325" }}>C</div>
            <span className="font-serif-display text-2xl leading-none">Coachly</span>
          </div>
        </div>
        <nav className="flex-1 py-4 px-3 space-y-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={l.testId}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                  isActive ? "bg-[#163325] text-[#f9f8f6]" : "text-[#3c3c3c] hover:bg-[#ebe8e0]"
                }`
              }
            >
              <l.icon className="w-4 h-4" strokeWidth={1.75} /> {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-[#e5e2da]">
          <div className="px-3 py-2 mb-2">
            <div className="text-sm font-medium truncate">{user?.name}</div>
            <div className="text-xs text-[#5c5c5c] truncate">{user?.email}</div>
          </div>
          <Button variant="ghost" onClick={handleLogout} data-testid="dashboard-logout-btn" className="w-full justify-start gap-2 text-[#3c3c3c] hover:bg-[#ebe8e0]">
            <LogOut className="w-4 h-4" /> Sign out
          </Button>
        </div>
      </aside>
      <main className="flex-1 overflow-auto">
        <EmailDeliveryBanner />
        <Outlet />
      </main>
    </div>
  );
}
